import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save, Upload, X, Image } from 'lucide-react';
import FileUpload from '../components/FileUpload';
import axios from 'axios';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const EditProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [formData, setFormData] = useState({
    display_name: '',
    handle: '',
    bio_richtext: '',
    avatar_url: '',
    banner_url: '',
    links: {
      instagram: '',
      twitter: '',
      youtube: '',
      website: ''
    },
    theme: {
      primary: '#f97316',
      accent: '#ef4444'
    },
    visibility: 'public'
  });

  useEffect(() => {
    if (!user || (user.role !== 'artist' && user.role !== 'author')) {
      toast.error('Only creators can edit profiles');
      navigate('/dashboard');
      return;
    }
    fetchProfile();
  }, [user, navigate]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/creators/me/profile`);
      const profile = response.data.profile;
      
      setProfileData(profile);
      setFormData({
        display_name: profile.display_name || '',
        handle: profile.handle || '',
        bio_richtext: profile.bio_richtext || '',
        avatar_url: profile.avatar_url || '',
        banner_url: profile.banner_url || '',
        links: {
          instagram: profile.links?.instagram || '',
          twitter: profile.links?.twitter || '',
          youtube: profile.links?.youtube || '',
          website: profile.links?.website || ''
        },
        theme: {
          primary: profile.theme?.primary || '#f97316',
          accent: profile.theme?.accent || '#ef4444'
        },
        visibility: profile.visibility || 'public'
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLinkChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      links: {
        ...prev.links,
        [platform]: value
      }
    }));
  };

  const handleThemeChange = (colorType, value) => {
    setFormData(prev => ({
      ...prev,
      theme: {
        ...prev.theme,
        [colorType]: value
      }
    }));
  };

  const handleFileUpload = (type, result) => {
    if (result && result.url) {
      setFormData(prev => ({
        ...prev,
        [`${type}_url`]: result.url
      }));
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully!`);
    }
  };

  const validateForm = () => {
    if (!formData.display_name.trim()) {
      toast.error('Display name is required');
      return false;
    }

    if (!formData.handle.trim()) {
      toast.error('Handle is required');
      return false;
    }

    // Validate handle format
    if (!/^[a-zA-Z0-9_]+$/.test(formData.handle)) {
      toast.error('Handle can only contain letters, numbers, and underscores');
      return false;
    }

    if (formData.handle.length < 3 || formData.handle.length > 30) {
      toast.error('Handle must be between 3 and 30 characters');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    
    try {
      // Clean up empty links
      const cleanLinks = {};
      Object.entries(formData.links).forEach(([platform, url]) => {
        if (url && url.trim()) {
          cleanLinks[platform] = url.trim();
        }
      });

      const updateData = {
        display_name: formData.display_name.trim(),
        handle: formData.handle.trim(),
        bio_richtext: formData.bio_richtext.trim(),
        avatar_url: formData.avatar_url,
        banner_url: formData.banner_url,
        links: cleanLinks,
        theme: formData.theme,
        visibility: formData.visibility
      };

      await axios.put(`${API}/creator/profile`, updateData);
      
      toast.success('Profile updated successfully! 🎉');
      navigate(`/creator/${formData.handle}`);
      
    } catch (error) {
      console.error('Error updating profile:', error);
      const message = error.response?.data?.detail || 'Failed to update profile';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl font-medium">Loading profile settings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur-sm border-b border-gray-800">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold">Edit Profile</h1>
            </div>
            
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images */}
          <div className="lg:col-span-1 space-y-6">
            {/* Banner Upload */}
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Image className="w-5 h-5 mr-2" />
                  Banner Image
                </CardTitle>
                <CardDescription>
                  Recommended: 1200x400px. This appears at the top of your profile.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FileUpload
                  type="banner"
                  currentUrl={formData.banner_url}
                  onUploadComplete={(result) => handleFileUpload('banner', result)}
                  className="w-full"
                />
              </CardContent>
            </Card>

            {/* Avatar Upload */}
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Image className="w-5 h-5 mr-2" />
                  Profile Avatar
                </CardTitle>
                <CardDescription>
                  Recommended: 400x400px square image.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FileUpload
                  type="avatar"
                  currentUrl={formData.avatar_url}
                  onUploadComplete={(result) => handleFileUpload('avatar', result)}
                  className="mx-auto"
                />
              </CardContent>
            </Card>

            {/* Theme Colors */}
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader>
                <CardTitle>Theme Colors</CardTitle>
                <CardDescription>
                  Customize your profile's accent colors
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="primary-color">Primary Color</Label>
                  <div className="flex items-center space-x-3 mt-2">
                    <input
                      type="color"
                      id="primary-color"
                      value={formData.theme.primary}
                      onChange={(e) => handleThemeChange('primary', e.target.value)}
                      className="w-12 h-12 rounded-lg border-2 border-gray-600 cursor-pointer"
                    />
                    <Input
                      value={formData.theme.primary}
                      onChange={(e) => handleThemeChange('primary', e.target.value)}
                      className="bg-gray-800 border-gray-600 text-white flex-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="accent-color">Accent Color</Label>
                  <div className="flex items-center space-x-3 mt-2">
                    <input
                      type="color"
                      id="accent-color"
                      value={formData.theme.accent}
                      onChange={(e) => handleThemeChange('accent', e.target.value)}
                      className="w-12 h-12 rounded-lg border-2 border-gray-600 cursor-pointer"
                    />
                    <Input
                      value={formData.theme.accent}
                      onChange={(e) => handleThemeChange('accent', e.target.value)}
                      className="bg-gray-800 border-gray-600 text-white flex-1"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Profile Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>
                  This information will be visible on your public profile
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="display-name">Display Name *</Label>
                  <Input
                    id="display-name"
                    value={formData.display_name}
                    onChange={(e) => handleInputChange('display_name', e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white mt-2"
                    placeholder="Your display name"
                    maxLength={50}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {formData.display_name.length}/50 characters
                  </div>
                </div>

                <div>
                  <Label htmlFor="handle">Handle * (@username)</Label>
                  <Input
                    id="handle"
                    value={formData.handle}
                    onChange={(e) => handleInputChange('handle', e.target.value.toLowerCase())}
                    className="bg-gray-800 border-gray-600 text-white mt-2"
                    placeholder="yourhandle"
                    maxLength={30}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    Your profile will be accessible at: trapcomix.com/creator/{formData.handle}
                  </div>
                </div>

                <div>
                  <Label htmlFor="bio">Biography</Label>
                  <Textarea
                    id="bio"
                    value={formData.bio_richtext}
                    onChange={(e) => handleInputChange('bio_richtext', e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white mt-2"
                    placeholder="Tell your audience about yourself and your work..."
                    rows={4}
                    maxLength={400}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {formData.bio_richtext.length}/400 characters
                  </div>
                </div>

                <div>
                  <Label htmlFor="visibility">Profile Visibility</Label>
                  <select
                    id="visibility"
                    value={formData.visibility}
                    onChange={(e) => handleInputChange('visibility', e.target.value)}
                    className="w-full p-2 bg-gray-800 border border-gray-600 rounded-md text-white mt-2"
                  >
                    <option value="public">Public - Anyone can view</option>
                    <option value="unlisted">Unlisted - Only accessible via direct link</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Social Links */}
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader>
                <CardTitle>Social Links</CardTitle>
                <CardDescription>
                  Connect your social media profiles (optional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="instagram">Instagram</Label>
                  <Input
                    id="instagram"
                    value={formData.links.instagram}
                    onChange={(e) => handleLinkChange('instagram', e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white mt-2"
                    placeholder="https://instagram.com/yourusername"
                  />
                </div>

                <div>
                  <Label htmlFor="twitter">Twitter/X</Label>
                  <Input
                    id="twitter"
                    value={formData.links.twitter}
                    onChange={(e) => handleLinkChange('twitter', e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white mt-2"
                    placeholder="https://twitter.com/yourusername"
                  />
                </div>

                <div>
                  <Label htmlFor="youtube">YouTube</Label>
                  <Input
                    id="youtube"
                    value={formData.links.youtube}
                    onChange={(e) => handleLinkChange('youtube', e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white mt-2"
                    placeholder="https://youtube.com/@yourchannel"
                  />
                </div>

                <div>
                  <Label htmlFor="website">Personal Website</Label>
                  <Input
                    id="website"
                    value={formData.links.website}
                    onChange={(e) => handleLinkChange('website', e.target.value)}
                    className="bg-gray-800 border-gray-600 text-white mt-2"
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Profile Preview */}
            <Card className="bg-gray-900 border-gray-700">
              <CardHeader>
                <CardTitle>Profile Preview</CardTitle>
                <CardDescription>
                  How your profile will appear to visitors
                </CardDescription>
              </CardHeader>
              <CardContent>
                {formData.handle ? (
                  <div className="text-center">
                    <p className="text-gray-400 mb-4">
                      Your profile will be live at:
                    </p>
                    <div className="bg-gray-800 p-3 rounded-lg">
                      <span className="text-orange-400 font-mono">
                        trapcomix.com/creator/{formData.handle}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center">
                    Enter a handle to see your profile URL
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;